import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Autocomplete, Box, Chip, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { CRM_CONTACT_QUICK_SEARCH } from './contacts.queries';
import { matchHints, MAX_SEARCH_LENGTH } from './contactList';

interface Suggestion {
  id: number;
  fullName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  city: string | null;
  countryCode: string | null;
  matchedOn: Array<string> | null;
  /** Card 20.5: flagged in the suggestion, before anyone opens the record. */
  doNotCall: boolean;
  doNotContact: boolean;
}

/** About 300 ms, as the API asks (§4.1): one request per pause, not per key. */
export const SUGGEST_DELAY_MS = 300;

/** crmContactSearch refuses fewer than 2 characters. */
const MIN_SUGGEST_LENGTH = 2;

const nameOf = (contact: Suggestion) => contact.fullName ?? contact.primaryEmail ?? `Contact #${contact.id}`;

interface ContactSearchBoxProps {
  /** The search the list is showing. */
  value: string;
  /** Enter: list every match. An empty text clears the search. */
  onSearch: (text: string) => void;
  /** A suggestion picked: open that contact. */
  onOpenContact: (contactId: number) => void;
  /** The API's refusal of the list's search, such as more than 8 words. */
  error?: string;
}

/**
 * The list's search box. Typing suggests up to 8 people (crmContactSearch);
 * Enter lists every match (filter.search). Both match the same way: words,
 * misspelt names, phone digits in any format, and exact secondary addresses.
 */
export const ContactSearchBox = ({ value, onSearch, onOpenContact, error }: ContactSearchBoxProps) => {
  const [input, setInput] = useState(value);
  const [asked, setAsked] = useState('');

  // The list's search changed from outside (cleared, back button): show it.
  useEffect(() => setInput(value), [value]);

  useEffect(() => {
    const timer = setTimeout(() => setAsked(input.trim()), SUGGEST_DELAY_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const suggest = asked.length >= MIN_SUGGEST_LENGTH && asked !== value;

  const { data, loading } = useQuery<{ crmContactSearch: { hasMore: boolean; items: Array<Suggestion> } }>(
    CRM_CONTACT_QUICK_SEARCH,
    {
      variables: { text: asked },
      skip: !suggest,
      // Never cached: its matchedOn would overwrite the cached rows the list shows.
      fetchPolicy: 'no-cache',
    }
  );

  const suggestions = suggest && input.trim() === asked ? data?.crmContactSearch.items ?? [] : [];
  const hasMore = Boolean(suggest && data?.crmContactSearch.hasMore);
  // Typed but not yet listed: say how to list it.
  const pending = Boolean(input.trim()) && input.trim().replace(/\s+/g, ' ') !== value;

  return (
    <Autocomplete<Suggestion, false, false, true>
      freeSolo
      options={suggestions}
      // The API has already matched and ordered them.
      filterOptions={(options) => options}
      getOptionLabel={(option) => (typeof option === 'string' ? option : nameOf(option))}
      value={null}
      inputValue={input}
      onInputChange={(_event, text, reason) => {
        if (reason === 'input') setInput(text);
        if (reason === 'clear') {
          setInput('');
          onSearch('');
        }
      }}
      onChange={(_event, picked) => {
        if (typeof picked === 'string') onSearch(picked.trim().replace(/\s+/g, ' '));
        else if (picked) onOpenContact(picked.id);
      }}
      loading={suggest && loading}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
              {nameOf(option)}
            </Typography>
            {(option.doNotContact || option.doNotCall) && (
              <Chip
                size="small"
                color="error"
                label={option.doNotContact ? 'Do not contact' : 'Do not call'}
                sx={{ ml: 1, height: 18, fontSize: '0.6875rem', fontWeight: 600 }}
              />
            )}
            {matchHints(option.matchedOn).map((hint) => (
              <Typography key={hint} variant="caption" color="warning.dark" component="span" sx={{ ml: 1 }}>
                {hint}
              </Typography>
            ))}
            <Typography variant="caption" color="text.secondary" component="div">
              {[option.primaryEmail, option.primaryPhone, [option.city, option.countryCode].filter(Boolean).join(', ')]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search by name, email, phone or city"
          error={Boolean(error)}
          helperText={error ?? (hasMore ? 'More people match: press Enter to list them all.' : undefined)}
          inputProps={{ ...params.inputProps, maxLength: MAX_SEARCH_LENGTH, 'aria-label': 'Search contacts' }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {pending && (
                  <Box
                    component="span"
                    aria-hidden
                    sx={{ display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center', gap: 0.75, mr: 1, color: 'text.secondary', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                  >
                    <Box
                      component="kbd"
                      sx={{
                        fontFamily: 'inherit',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        border: '1px solid #d6dce6',
                        borderBottomWidth: 2,
                        bgcolor: '#f8fafc',
                      }}
                    >
                      Enter
                    </Box>
                    to list every match
                  </Box>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: { minHeight: 46, fontSize: '0.9375rem', borderRadius: 2.5, bgcolor: '#f8fafc' },
          }}
        />
      )}
    />
  );
};
